import {
  TransportRequestError,
  useTransportPaginatedQuery,
  useTransportQuery,
  type TransportResponse,
} from "@/transport"

import type { TransportOperationRequest } from "@/types_api/backend/transport/operations"
import type { OtelTrace, TraceSpan, TraceTreeResponse } from "@/types/OtelTrace"

import { normalizeTraceTimestamp } from "../viewer/utils/traceTimestamps"

const NS_PER_MS = 1_000_000
const TRACE_SPAN_KEY_SEPARATOR = ":"
export const SIMULATION_TRACES_PAGE_SIZE = 25

type SimulationTracesResponse = TransportResponse<"listSimulationTraces">
type SimulationTracesRequest = TransportOperationRequest<"listSimulationTraces">
type SimulationTraceRecord = SimulationTracesResponse["traces"][number]

interface TraceReadQueryOptions {
  enabled?: boolean
  gcTime?: number
  refetchInterval?: false | number
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  staleTime?: number
}

function getTraceSpanKey(trace: Pick<OtelTrace, "trace_id" | "span_id">) {
  return `${trace.trace_id}${TRACE_SPAN_KEY_SEPARATOR}${trace.span_id}`
}

function getParentTraceSpanKey(trace: OtelTrace) {
  return `${trace.trace_id}${TRACE_SPAN_KEY_SEPARATOR}${trace.parent_span_id}`
}

function normalizeTraceString(value: unknown): string {
  if (value == null) {
    return ""
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value)
  }

  return ""
}

function normalizeTraceAttributes(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, attributeValue]) => [
      key,
      normalizeTraceString(attributeValue),
    ]),
  )
}

function normalizeTraceRecord(record: SimulationTraceRecord): OtelTrace {
  return {
    organization_id: normalizeTraceString(record.organization_id),
    run_id: normalizeTraceString(record.run_id),
    simulation_output_id: normalizeTraceString(record.simulation_output_id),
    trace_id: normalizeTraceString(record.trace_id),
    span_id: normalizeTraceString(record.span_id),
    parent_span_id: normalizeTraceString(record.parent_span_id),
    span_name: normalizeTraceString(record.span_name),
    span_kind: normalizeTraceString(record.span_kind),
    service_name: normalizeTraceString(record.service_name),
    scope_name: normalizeTraceString(record.scope_name),
    scope_version: normalizeTraceString(record.scope_version),
    timestamp: normalizeTraceTimestamp(normalizeTraceString(record.timestamp)),
    duration_ns: normalizeTraceString(record.duration_ns),
    status_code: normalizeTraceString(record.status_code),
    status_message: normalizeTraceString(record.status_message),
    span_attributes: normalizeTraceAttributes(record.span_attributes),
    resource_attributes: normalizeTraceAttributes(record.resource_attributes),
    events: normalizeTraceString(record.events),
    links: normalizeTraceString(record.links),
    inserted_at: normalizeTraceString(record.inserted_at),
  }
}

function normalizeTraceRecords(records: SimulationTraceRecord[]): OtelTrace[] {
  return records.map(normalizeTraceRecord)
}

export function buildTraceTree(traces: OtelTrace[]): TraceSpan[] {
  const spanMap = new Map<string, TraceSpan>()
  const rootSpans: TraceSpan[] = []

  for (const trace of traces) {
    const parsedDurationNs = Number(trace.duration_ns)
    const durationMs =
      Number.isFinite(parsedDurationNs) && parsedDurationNs >= 0
        ? parsedDurationNs / NS_PER_MS
        : 0
    spanMap.set(getTraceSpanKey(trace), {
      ...trace,
      children: [],
      depth: 0,
      durationMs,
    })
  }

  for (const trace of traces) {
    const span = spanMap.get(getTraceSpanKey(trace))!
    const parentKey = getParentTraceSpanKey(trace)
    const hasExternalParent =
      !!trace.parent_span_id && trace.parent_span_id !== trace.span_id

    if (hasExternalParent && spanMap.has(parentKey)) {
      spanMap.get(parentKey)!.children.push(span)
    } else {
      rootSpans.push(span)
    }
  }

  function setDepths(span: TraceSpan, depth: number) {
    span.depth = depth
    for (const child of span.children) {
      setDepths(child, depth + 1)
    }
  }

  function sortChildren(span: TraceSpan) {
    span.children.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
    for (const child of span.children) {
      sortChildren(child)
    }
  }

  for (const root of rootSpans) {
    setDepths(root, 0)
    sortChildren(root)
  }

  rootSpans.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  )

  return rootSpans
}

export function normalizeTraceResponse(
  response: SimulationTracesResponse,
): TraceTreeResponse {
  const traces = normalizeTraceRecords(response.traces)
  return {
    traces,
    traceTree: buildTraceTree(traces),
    totalSpans: response.total_spans,
  }
}

export function combineTracePages(
  pages: SimulationTracesResponse[],
): TraceTreeResponse {
  const traces = normalizeTraceRecords(pages.flatMap((page) => page.traces))
  const totalSpans = pages.at(-1)?.total_spans ?? 0

  return {
    traces,
    traceTree: buildTraceTree(traces),
    totalSpans,
  }
}

export function getNextTracePageRequest<
  TRequest extends Pick<SimulationTracesRequest, "offset">,
>(
  lastPage: SimulationTracesResponse,
  previousRequest: TRequest | undefined,
): TRequest | null {
  if (!previousRequest) {
    return null
  }

  const tracesLoaded = lastPage.traces.length
  const currentOffset = previousRequest.offset ?? 0
  const nextOffset = currentOffset + tracesLoaded

  if (tracesLoaded === 0 || nextOffset >= lastPage.total_spans) {
    return null
  }

  return {
    ...previousRequest,
    offset: nextOffset,
  }
}

export function useSimulationTracePresenceTransport(
  simulationOutputId: string | undefined,
  options: TraceReadQueryOptions = {},
) {
  return useTransportQuery({
    operation: "hasSimulationTraces",
    request: simulationOutputId
      ? { simulation_output_id: simulationOutputId }
      : undefined,
    options: {
      ...options,
      enabled: (options.enabled ?? true) && !!simulationOutputId,
    },
  })
}

export function useRunTracePresenceTransport(
  runId: string | undefined,
  options: TraceReadQueryOptions = {},
) {
  return useTransportQuery({
    operation: "hasRunTraces",
    request: runId ? { run_id: runId } : undefined,
    options: {
      ...options,
      enabled: (options.enabled ?? true) && !!runId,
    },
  })
}

export function useSimulationTracesTransport(
  simulationOutputId: string | undefined,
  options: TraceReadQueryOptions = {},
) {
  return useTransportPaginatedQuery<"listSimulationTraces", TraceTreeResponse>({
    operation: "listSimulationTraces",
    request: simulationOutputId
      ? {
          limit: SIMULATION_TRACES_PAGE_SIZE,
          offset: 0,
          simulation_output_id: simulationOutputId,
        }
      : undefined,
    combinePages: combineTracePages,
    getNextRequest: getNextTracePageRequest,
    getPageItemCount: (page) => page.traces.length,
    getTotalItemCount: (page) => page.total_spans,
    options: {
      ...options,
      enabled: (options.enabled ?? true) && !!simulationOutputId,
    },
  })
}

/**
 * Retries a shared trace read after a transient failure, not after a refusal:
 * a wrong password or an unshared result answers 4xx every time.
 */
export function retrySharedTraceRead(failureCount: number, error: unknown) {
  const refused =
    error instanceof TransportRequestError &&
    error.status >= 400 &&
    error.status < 500
  return !refused && failureCount < 2
}

/**
 * Span count for a shared result. Shared links carry traces only when the run
 * is password-protected, so there is nothing to ask without the password.
 */
export function useSharedSimulationTracePresenceTransport(
  simulationOutputId: string | undefined,
  password: string | null | undefined,
  options: TraceReadQueryOptions = {},
) {
  return useTransportQuery({
    operation: "listSharedSimulationTraces",
    request:
      simulationOutputId && password
        ? {
            limit: 1,
            offset: 0,
            password,
            simulation_output_id: simulationOutputId,
          }
        : undefined,
    options: {
      ...options,
      enabled: (options.enabled ?? true) && !!simulationOutputId && !!password,
      retry: retrySharedTraceRead,
      select: (response) => ({
        has_traces: response.total_spans > 0,
        span_count: response.total_spans,
      }),
    },
  })
}

export function useSharedSimulationTracesTransport(
  simulationOutputId: string | undefined,
  password: string | null | undefined,
  options: TraceReadQueryOptions = {},
) {
  return useTransportPaginatedQuery<
    "listSharedSimulationTraces",
    TraceTreeResponse
  >({
    operation: "listSharedSimulationTraces",
    request:
      simulationOutputId && password
        ? {
            limit: SIMULATION_TRACES_PAGE_SIZE,
            offset: 0,
            password,
            simulation_output_id: simulationOutputId,
          }
        : undefined,
    combinePages: combineTracePages,
    getNextRequest: getNextTracePageRequest,
    getPageItemCount: (page) => page.traces.length,
    getTotalItemCount: (page) => page.total_spans,
    options: {
      ...options,
      enabled: (options.enabled ?? true) && !!simulationOutputId && !!password,
      retry: retrySharedTraceRead,
    },
  })
}

/** Where a trace's recording lives and when it starts, for the trace viewer. */
export interface TraceAudioContext {
  simulationS3Bucket: string | null
  simulationS3Key: string | null
  audioStartTime: string | null
  /** Whether audioStartTime marks the recording's start rather than the job's. */
  audioStartIsRecordingStart: boolean
  audioFileDurationSeconds: number | null
}

export function useTraceAudioContextTransport(
  simulationOutputId: string | undefined,
  options: TraceReadQueryOptions = {},
) {
  return useTransportQuery({
    operation: "getTraceAudioContext",
    request: { simulation_output_id: simulationOutputId ?? "" },
    options: {
      ...options,
      enabled: (options.enabled ?? true) && Boolean(simulationOutputId),
      select: (response): TraceAudioContext => ({
        simulationS3Bucket: response.simulation_s3_bucket ?? null,
        simulationS3Key: response.simulation_s3_key ?? null,
        audioStartTime:
          response.recording_start_time ??
          response.simulation_start_time ??
          null,
        audioStartIsRecordingStart: response.recording_start_time != null,
        audioFileDurationSeconds: response.audio_file_duration_seconds ?? null,
      }),
    },
  })
}
